import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import {
    MonitorCog , SquareActivity , Tickets , Menu, X, Home, Building, Bell, Search, RefreshCw, LogOut, ChevronRight,
    Plus, Code, Folder, Calendar, Users, CheckCircle, Activity,
    TrendingUp, Filter, Download, AlertCircle, Calculator, BarChart3, UserCog , Eye , Trash2 , FileText , Receipt , Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../Styles/dashboard.css';
import '../Styles/project_management.css';
import MepIcon from '../assets/MEP_Solutions.png';
import architecturalIcon from '../assets/architectural_icon.png';
import civilIcon from '../assets/civil-icon.png';
import constructionIcon from '../assets/construction-icon.png';
import powerToolsIcon from '../assets/power-tools_icon.png';
import sustainableIcon from '../assets/sustainable-icon.png';
import hygienicIcon from '../assets/hygienic-icon.png';

import ProjectGrid from './components/ProjectGrid';
import CreateProjectModal from './components/CreateProjectModal';
import JoinProjectModal from './components/JoinProjectModal';
import DeleteProjectModal from './components/DeleteProjectModal';

const ProjectManagement = () => {
    const [user, setUser] = useState(null);
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenuItem, setActiveMenuItem] = useState('projects');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [notifications, setNotifications] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    // Project Management States
    const [projects, setProjects] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showJoinForm, setShowJoinForm] = useState(false);
    const [filterTag, setFilterTag] = useState('');
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [error, setError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    useEffect(() => {
        loadCurrentUser();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (user) {
            loadProjects();
        }
    }, [user, searchTerm, filterTag]);

    const loadCurrentUser = async () => {
        try {
            setIsAuthChecking(true);

            const syncUser = ApiService.getUserSync();
            if (syncUser) {
                setUser(syncUser);
                setIsAuthChecking(false);
                return;
            }

            const response = await ApiService.getCurrentUser();
            if (response.success && response.user) {
                setUser(response.user);
            } else {
                navigate('/login');
            }
        } catch (error) {
            console.error('Failed to load user:', error);
            navigate('/login');
        } finally {
            setIsAuthChecking(false);
        }
    };
    const confirmDeleteProject = async (projectId, projectName) => {
        try {
            setIsLoading(true);
            const response = await ApiService.deleteProject(projectId);

            if (response.success) {
                setProjects(prev => prev.filter(project => project._id !== projectId));
                setShowDeleteModal(false);
                setProjectToDelete(null);
                alert(`Project "${projectName}" has been successfully deleted.`);
            } else {
                throw new Error(response.message || 'Failed to delete project');
            }
        } catch (error) {
            console.error('Error deleting project:', error);
            alert(`Error deleting project: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    const loadProjects = async () => {
        try {
            setIsLoadingProjects(true);
            setError(null);

            const params = {
                search: searchTerm || undefined,
                tag: filterTag || undefined
            };

            Object.keys(params).forEach(key => {
                if (params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await ApiService.getProjects(params);
            if (response.success) {
                setProjects(response.projects || []);
            } else {
                setError('Erreur lors du chargement des projets');
            }
        } catch (error) {
            console.error('Failed to load projects:', error);
            setError('Erreur lors du chargement des projets');
            setProjects([]);
        } finally {
            setIsLoadingProjects(false);
        }
    };

    const handleDeleteProject = async (projectId, projectName) => {
        const project = projects.find(p => p._id === projectId);

        if (!project) {
            alert('Project not found.');
            return;
        }

        // Check if user is the owner
        const isOwner = project.owner === user._id || project.owner?._id === user._id;
        const isAdmin = user.role === 'admin' || user.role === 'super_admin';

        if (!isOwner && !isAdmin) {
            alert('Access Denied: Only the project owner or administrators can delete this project.');
            return;
        }

        // For admins deleting projects they don't own, show additional warning
        if (!isOwner && isAdmin) {
            const adminConfirm = confirm(
                `⚠️ ADMIN ACTION ⚠️\n\n` +
                `You are about to delete a project you don't own:\n` +
                `Project: "${projectName}"\n` +
                `Owner: ${project.owner?.firstName} ${project.owner?.lastName} (${project.owner?.email})\n\n` +
                `Are you sure you want to proceed as an administrator?`
            );

            if (!adminConfirm) {
                return;
            }
        }

        setProjectToDelete(project);
        setShowDeleteModal(true);
    };

    const handleCreateProject = async (projectData) => {
        try {
            const response = await ApiService.createProject(projectData);
            if (response.success) {
                setProjects(prev => [response.project, ...prev]);
                setShowCreateForm(false);
                alert(`Project created! Share this code with your team: ${response.project.code}`);
                return { success: true };
            } else {
                return { success: false, message: response.message };
            }
        } catch (error) {
            console.error('Error creating project:', error);
            return { success: false, message: error.message };
        }
    };

    const handleJoinProject = async (code) => {
        try {
            const response = await ApiService.joinProject(code);
            if (response.success) {
                setProjects(prev => [response.project, ...prev]);
                setShowJoinForm(false);
                return { success: true, message: 'Successfully joined the project!' };
            } else {
                return { success: false, message: response.message };
            }
        } catch (error) {
            console.error('Error joining project:', error);
            return { success: false, message: error.message };
        }
    };

    const handleRefresh = () => {
        loadProjects();
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you wanna disconnect?')) {
            ApiService.clearUserData();
            navigate('/login');
        }
    };

    const getAllTags = () => {
        const allTags = projects.flatMap(project => project.tags || []);
        return [...new Set(allTags)];
    };

    const menuItems = [
        { id: 'barcoding', label: 'Barcoding Database', icon: 'Folder', path: '/barcoding' },
        { id: 'mep-solutions', label: 'MEP Solutions', icon: MepIcon, isImage: true, path: '/mep_solutions' },
        { id: 'architectural-solutions', label: 'Architectural Solutions', icon: architecturalIcon, isImage: true, path: '/architectural_solutions' },
        { id: 'civil-solutions', label: 'Civil Solutions', icon: civilIcon, isImage: true, path: '/civil_solutions' },
        { id: 'construction-chemicals', label: 'Construction Chemicals', icon: constructionIcon, isImage: true, path: '/construction_chemicals' },
        { id: 'power-tools', label: 'Power & Industrial Tools', icon: powerToolsIcon, isImage: true, path: '/power_tools' },
        { id: 'sustainable-solutions', label: 'Sustainable Solutions', icon: sustainableIcon, isImage: true, path: '/sustainable_solutions' },
        { id: 'hygienic-solutions', label: 'Hygienic Solutions', icon: hygienicIcon, isImage: true, path: '/hygienic_solutions' },
        { id: 'conversions', label: 'Engineering Conversions', icon: Calculator, path: '/conversion' },
        { id: 'analytics', label: 'Amazon Products', icon: BarChart3, path: '/amazon_products' },
        { id: 'team', label: 'Team Management', icon: Users, path: '/team_management' },
        { id: 'user-management', label: 'User Management', icon: UserCog, path: '/users_management' },
        { id: 'projects', label: 'Project Management', icon: Folder, path: '/projects' }
    ];

    const filteredMenuItems = menuItems.filter(item => {
        if (item.id === 'team' && user?.role === 'employee') return false;
        return true;
    });

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    if (isAuthChecking) {
        return (
            <div className="loading-screen">
                <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">UNITECH IKK is Loading</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <>
            <div className="dashboard-container">
                {isLoading && (
                    <div className="loading-overlay">
                        <div className="loading-overlay-content">
                            <div className="loading-overlay-spinner"></div>
                            <span className="loading-overlay-text">Data is Loading</span>
                        </div>
                    </div>
                )}

                {/* Sidebar */}
                <aside className="sidebar">
                    <div className="sidebar-header">
                        <div
                            className="sidebar-logo-container"
                            onClick={() => navigate('/dashboard')}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderRadius: '8px',
                                padding: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Retour au Dashboard"
                        >
                            <div className="sidebar-logo">
                                <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                            <div className="sidebar-brand">
                                <h1 className="sidebar-title">UNITECH IKK</h1>
                                <p className="sidebar-subtitle">Enterprise Dashboard</p>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        {filteredMenuItems.map((item) => (
                            <div
                                key={item.id}
                                className={`nav-item ${activeMenuItem === item.id ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveMenuItem(item.id);
                                    navigate(item.path);
                                }}
                            >
                                {item.isImage ? (
                                    <img
                                        src={item.icon}
                                        alt={item.label}
                                        className="nav-icon-image"
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            display: 'block'
                                        }}
                                    />
                                ) : (
                                    <item.icon
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            display: 'block'
                                        }}
                                    />
                                )}
                                <span className="nav-text">{item.label}</span>
                            </div>
                        ))}
                    </nav>

                    <div className="sidebar-footer">
                        <div className="user-profile">
                            <div className="user-avatar">{getUserInitials()}</div>
                            <div className="user-info">
                                <p className="user-name">{getDisplayName()}</p>
                                <p className="user-role">{user?.role || 'Utilisateur'}</p>
                            </div>
                            <button
                                className="logout-button"
                                onClick={handleLogout}
                                title="Logout"
                            >
                                <LogOut style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="main-content">
                    {/* Header */}
                    <header className="header">
                        <div className="header-content">
                            <div className="header-left">
                                <button
                                    className="menu-toggle"
                                    onClick={() => setSidebarOpen(!sidebarOpen)}
                                >
                                    {sidebarOpen ?
                                        <X style={{ width: '20px', height: '20px' }} /> :
                                        <Menu style={{ width: '20px', height: '20px' }} />
                                    }
                                </button>

                                <nav className="breadcrumb">
                                    <div className="breadcrumb-item">
                                        <Home style={{ width: '16px', height: '16px' }} />
                                        <span>Dashboard</span>
                                    </div>
                                    <ChevronRight style={{ width: '14px', height: '14px' }} />
                                    <div className="breadcrumb-item">
                                        <span>Project Management</span>
                                    </div>
                                </nav>
                            </div>

                            <div className="header-right">
                                <div className="search-container">
                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Rechercher des projets..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                                </div>

                                <div className="header-actions">
                                    <button
                                        className="header-button"
                                        onClick={handleRefresh}
                                        disabled={isLoadingProjects}
                                        title="Actualiser"
                                    >
                                        <RefreshCw style={{
                                            width: '18px',
                                            height: '18px',
                                            transform: isLoadingProjects ? 'rotate(360deg)' : 'none',
                                            transition: 'transform 1s linear'
                                        }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        title="Notifications"
                                    >
                                        <Bell style={{ width: '18px', height: '18px' }} />
                                        {notifications > 0 && (
                                            <span className="notification-badge">{notifications}</span>
                                        )}
                                    </button>

                                    <div className="time-display">
                                        {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Dashboard Content */}
                    <div className="dashboard-content">
                        {/* Error Display */}
                        {error && (
                            <div className="error-message" style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#dc2626',
                                padding: '12px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <AlertCircle style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                {error}
                            </div>
                        )}

                        {/* Page Header */}
                        <div className="page-header">
                            <h1 className="page-title">Gestion des Projets</h1>
                            <p className="page-subtitle">
                                Créez de nouveaux projets ou rejoignez des équipes existantes pour collaborer efficacement.
                            </p>

                            <div className="dashboard-actions">
                                <button
                                    className="action-button"
                                    onClick={() => setShowCreateForm(true)}
                                >
                                    <Plus style={{ width: '16px', height: '16px' }} />
                                    Nouveau Projet
                                </button>
                                <button
                                    className="action-button secondary"
                                    onClick={() => setShowJoinForm(true)}
                                >
                                    <Code style={{ width: '16px', height: '16px' }} />
                                    Rejoindre un Projet
                                </button>
                                {getAllTags().length > 0 && (
                                    <select
                                        value={filterTag}
                                        onChange={(e) => setFilterTag(e.target.value)}
                                        className="action-button secondary"
                                        style={{ padding: '8px 12px' }}
                                    >
                                        <option value="">Tous les Tags</option>
                                        {getAllTags().map(tag => (
                                            <option key={tag} value={tag}>{tag}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-header">
                                    <div className="stat-icon blue">
                                        <Folder style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div className="stat-change">
                                        <TrendingUp style={{ width: '14px', height: '14px' }} />
                                        +12%
                                    </div>
                                </div>
                                <div className="stat-value">{projects.length}</div>
                                <div className="stat-label">Total Projects</div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-header">
                                    <div className="stat-icon green">
                                        <Activity style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div className="stat-change">
                                        <TrendingUp style={{ width: '14px', height: '14px' }} />
                                        +8%
                                    </div>
                                </div>
                                <div className="stat-value">{projects.filter(p => p.isActive).length}</div>
                                <div className="stat-label">Active Projects</div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-header">
                                    <div className="stat-icon purple">
                                        <Users style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div className="stat-change">
                                        <TrendingUp style={{ width: '14px', height: '14px' }} />
                                        +15%
                                    </div>
                                </div>
                                <div className="stat-value">{projects.reduce((acc, p) => acc + (p.members?.length || 0), 0)}</div>
                                <div className="stat-label">Team Members</div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-header">
                                    <div className="stat-icon orange">
                                        <CheckCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div className="stat-change">
                                        <TrendingUp style={{ width: '14px', height: '14px' }} />
                                        +22%
                                    </div>
                                </div>
                                <div className="stat-value">89%</div>
                                <div className="stat-label">Completion Rate</div>
                            </div>
                        </div>

                        {/* Project Grid Component */}
                        <ProjectGrid
                            projects={projects}
                            isLoading={isLoadingProjects}
                            searchTerm={searchTerm}
                            filterTag={filterTag}
                            onRefresh={handleRefresh}
                            onDeleteProject={handleDeleteProject} // Add this
                            currentUser={user}                    // Add this
                            navigate={navigate}
                        />

                        {/* Status Footer */}
                        <div className="status-footer">
                            <div className="status-indicator">
                                <div className="status-operational">
                                    <div className="status-dot"></div>
                                    <Activity style={{ width: '16px', height: '16px' }} />
                                    All Systems are Running
                                </div>
                            </div>

                            <div className="status-info">
                                <span>Dernière mise à jour: {currentTime.toLocaleTimeString('fr-FR')}</span>
                                <span>•</span>
                                <span>Version 1.0</span>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            <CreateProjectModal
                show={showCreateForm}
                onClose={() => setShowCreateForm(false)}
                onSubmit={handleCreateProject}
            />

            <JoinProjectModal
                show={showJoinForm}
                onClose={() => setShowJoinForm(false)}
                onSubmit={handleJoinProject}
            />

            <DeleteProjectModal
                show={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setProjectToDelete(null);
                }}
                onConfirm={confirmDeleteProject}
                project={projectToDelete}
                currentUser={user}
            />
        </>
    );
};

export default ProjectManagement;